import { useActionSheet } from '@expo/react-native-action-sheet';
import { Image } from 'expo-image';
import * as WebBrowser from 'expo-web-browser';
import { useEffect, useRef, useState } from 'react';
import { Linking, Platform, Pressable, ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { Car } from '@/api/cars/types';
import { useVerifyVirtualKey } from '@/api/partners/cars/use-verify-virtual-key';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { getMobileWebOS, isDesktop } from '@/core';
import { Button, Card, Text } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';
import { Chip, ChipType } from '@/ui/chip';
import { Icon } from '@/ui/icon';
import { Checkmark } from '@/ui/icons/checkmark';
import { ProgressIndicator } from '@/ui/progress-indicator';

const tutorial1 = require('../../../../public/images/setup-digital-key/tutorial-1.png');
const tutorial2 = require('../../../../public/images/setup-digital-key/tutorial-2.png');
const tutorial3 = require('../../../../public/images/setup-digital-key/tutorial-3.png');
const tutorial4 = require('../../../../public/images/setup-digital-key/tutorial-4.png');
const tutorial5 = require('../../../../public/images/setup-digital-key/tutorial-5.png');
const tutorial6 = require('../../../../public/images/setup-digital-key/tutorial-6.png');
const tutorial7 = require('../../../../public/images/setup-digital-key/tutorial-7.png');
const tutorialVideo = require('../../../../public/images/setup-digital-key/tutorial-video.gif');

const mobileWebOS = getMobileWebOS();

interface SetupDigitalKeyModalProps {
  visible?: boolean;
  onClose: () => void;
  onVerifyKeySuccess?: () => void;
  style?: any;
  car?: Car;
}

export function SetupDigitalKeyModal({
  visible = false,
  onClose,
  onVerifyKeySuccess,
  style,
  car,
}: SetupDigitalKeyModalProps) {
  const { styles, theme } = useStyles(stylesheet);
  const { showActionSheetWithOptions } = useActionSheet();
  const scrollViewRef = useRef<ScrollView>(null);
  const [showModal, setShowModal] = useState(visible);
  const [currentStep, setCurrentStep] = useState(1);
  const [verifyVirtualKeyError, setVerifyVirtualKeyError] = useState<
    string | null
  >(null);
  const [showDefaultFlow, setShowDefaultFlow] = useState(true);
  const [showGetEonAppFlow, setShowGetEonAppFlow] = useState(false);

  const { mutateAsync: verifyVirtualKey, isPending: isVerifyingVirtualKey } =
    useVerifyVirtualKey();

  useEffect(() => {
    setShowModal(visible);
  }, [visible]);

  useEffect(() => {
    if (currentStep === 2 || currentStep === 3) {
      scrollViewRef.current?.scrollTo({ y: 0, animated: false });
    }
  }, [currentStep]);

  useEffect(() => {
    if (!showModal) {
      setCurrentStep(1);

      if (mobileWebOS === 'ios' || isDesktop()) {
        setShowDefaultFlow(false);
      } else {
        setShowDefaultFlow(true);
      }

      setShowGetEonAppFlow(false);
    }
  }, [showModal]);

  useEffect(() => {
    if (mobileWebOS === 'ios' || isDesktop()) {
      setShowDefaultFlow(false);
    }
  }, []);

  const handleCreateDigitalKey = async () => {
    const baseUrl = 'https://www.tesla.com/_ak/app.standardfleet.com';

    if (Platform.OS === 'web') {
      window.open(baseUrl, '_blank');
    } else {
      await WebBrowser.openBrowserAsync(baseUrl);
    }
  };

  const handleTestDigitalKey = async () => {
    try {
      await verifyVirtualKey({
        id: car.id,
      });

      onVerifyKeySuccess?.();

      Toast.show({
        text1: 'Key connected successfully!',
        type: 'success',
      });
    } catch (error: any) {
      console.log('Error verifying digital key', error);
      setVerifyVirtualKeyError(error.message || 'Unknown error');
    }
  };

  const renderBody = () => {
    if (showGetEonAppFlow) {
      return (
        <View style={{ height: '100%' }}>
          <View
            style={{
              minHeight: 50,
              flexDirection: 'row',
              paddingLeft: 16,
              paddingTop: 16,
            }}
          >
            <View>
              <Text type="title2">Get The Eon App</Text>
            </View>

            <NavigationButton
              type={NavigationButtonTypes.cancel}
              onPress={() => {
                setShowModal(false);
                onClose?.();
              }}
              style={{
                position: 'absolute',
                right: 16,
                top: 16,
              }}
            />
          </View>

          <View style={{ flex: 1, padding: 16, gap: 16 }}>
            <View>
              <Alert
                type={AlertType.BRAND}
                title="Cars might take up 10 minutes to connect"
              />
            </View>

            <View style={styles.qrContainer}>
              <Card
                style={{
                  backgroundColor: theme.colors.card.hover.backgroundColor,
                  padding: 16,
                }}
              >
                <View style={{ alignItems: 'center', gap: 16 }}>
                  <Image
                    style={{
                      width: 100,
                      height: 100,
                    }}
                    source={
                      UnistylesRuntime.themeName === 'dark'
                        ? require('@/../public/images/qr-onelink-dark.png')
                        : require('@/../public/images/qr-onelink-light.png')
                    }
                    contentFit="cover"
                  />

                  <Text type="bodyRegular">Download the app</Text>

                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Connecting Digital Key only available on iOS 14 or later
                  </Text>
                </View>
              </Card>
            </View>

            <View style={{ gap: 16, flexGrow: 1 }}>
              <View>
                <Text type="bodyRegular">
                  How to Access Partner Portal in the Eon App
                </Text>
              </View>

              <View style={{ gap: 16 }}>
                <View
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
                >
                  <View
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 12,
                      backgroundColor:
                        theme.colors.progressBar.active.backgroundColor,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.inverse }}
                    >
                      1
                    </Text>
                  </View>

                  <Text
                    type="bodyRegular"
                    style={{ flex: 1, color: theme.colors.text.secondary }}
                  >
                    Go to Account in the app
                  </Text>
                </View>

                <View
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
                >
                  <View
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 12,
                      backgroundColor:
                        theme.colors.progressBar.active.backgroundColor,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.inverse }}
                    >
                      2
                    </Text>
                  </View>

                  <Text
                    type="bodyRegular"
                    style={{ flex: 1, color: theme.colors.text.secondary }}
                  >
                    Scroll down to find partner options
                  </Text>
                </View>

                <View
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
                >
                  <View
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 12,
                      backgroundColor:
                        theme.colors.progressBar.active.backgroundColor,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.inverse }}
                    >
                      3
                    </Text>
                  </View>

                  <Text
                    type="bodyRegular"
                    style={{ flex: 1, color: theme.colors.text.secondary }}
                  >
                    Tap the “Switch to Partners” button to switch views
                  </Text>
                </View>
              </View>

              <View style={{ gap: 16 }}>
                <Text type="bodyRegular">How To Connect Cars</Text>

                <View
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
                >
                  <View
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 12,
                      backgroundColor:
                        theme.colors.progressBar.active.backgroundColor,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.inverse }}
                    >
                      1
                    </Text>
                  </View>

                  <Text
                    type="bodyRegular"
                    style={{ flex: 1, color: theme.colors.text.secondary }}
                  >
                    Go to Cars tab
                  </Text>
                </View>

                <View
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
                >
                  <View
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 12,
                      backgroundColor:
                        theme.colors.progressBar.active.backgroundColor,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.inverse }}
                    >
                      2
                    </Text>
                  </View>

                  <Text
                    type="bodyRegular"
                    style={{ flex: 1, color: theme.colors.text.secondary }}
                  >
                    Click "Fix Issue"
                  </Text>
                </View>
              </View>
            </View>

            <View style={styles.downloadApp}>
              <Button
                text="Download App"
                type="primary"
                onPress={async () => {
                  await WebBrowser.openBrowserAsync(
                    'https://appeon.onelink.me/nQbG/2an6kaz8'
                  );
                }}
                style={{ width: '100%' }}
              />
            </View>
          </View>
        </View>
      );
    }

    if (!showDefaultFlow && (isDesktop() || mobileWebOS === 'ios')) {
      return (
        <View style={{ height: '100%' }}>
          <View
            style={{
              minHeight: 50,
              flexDirection: 'row',
              paddingLeft: 16,
              paddingTop: 16,
            }}
          >
            <View>
              <Text type="title2">Create & Connect Digital Key</Text>
            </View>

            <NavigationButton
              type={NavigationButtonTypes.cancel}
              onPress={() => {
                setShowModal(false);
                onClose?.();
              }}
              style={{
                position: 'absolute',
                right: 16,
                top: 16,
              }}
            />
          </View>

          <ScrollView
            style={{ flex: 1, padding: 16 }}
            contentContainerStyle={{ flexGrow: 1, gap: 16 }}
          >
            <View style={{ gap: 16 }}>
              <Text type="title2">What Is A Digital Key?</Text>

              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                Digital Keys are the way Tesla enables 3rd party systems (like
                Eon) to connect and communicate with your car. Data like
                mileage, charging, and vehicle control all pass through the
                Digital Key.
              </Text>
            </View>

            <Text type="bodyRegular">Choose how to connect</Text>

            <View style={{}}>
              <Card>
                <View style={{ padding: 16, gap: 16 }}>
                  <View style={{ gap: 4 }}>
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text type="title2">Use The IOS App</Text>

                      <Chip type={ChipType.BRAND} text="Fastest" />
                    </View>

                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      Quick & easy setup. Connect all your cars at once from the
                      app.
                    </Text>
                  </View>

                  <View style={{ gap: 4 }}>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <Icon
                        type="naked"
                        icon={<Checkmark />}
                        stroke={theme.colors.success}
                        fillColor={theme.colors.success}
                        width={12}
                        height={12}
                      />

                      <Text type="caption1">2-minute setup</Text>
                    </View>

                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <Icon
                        type="naked"
                        icon={<Checkmark />}
                        stroke={theme.colors.success}
                        fillColor={theme.colors.success}
                        width={12}
                        height={12}
                      />

                      <Text type="caption1">Sync entire fleet</Text>
                    </View>

                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <Icon
                        type="naked"
                        icon={<Checkmark />}
                        stroke={theme.colors.success}
                        fillColor={theme.colors.success}
                        width={12}
                        height={12}
                      />

                      <Text type="caption1">Auto-updates</Text>
                    </View>
                  </View>

                  <View>
                    <Button
                      text="Connect With App"
                      type="primary"
                      onPress={() => {
                        setShowGetEonAppFlow(true);
                      }}
                      style={{ width: '100%' }}
                    />
                  </View>

                  <View>
                    <Text
                      type="caption1"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      Connecting Digital Key only available on iOS 14 or later
                    </Text>
                  </View>
                </View>
              </Card>
            </View>

            <View>
              <Text
                type="caption1"
                style={{
                  alignSelf: 'center',
                  color: theme.colors.text.secondary,
                }}
              >
                or
              </Text>
            </View>

            <View>
              <Button
                text="Connect Each Car Individually"
                type="ghost"
                onPress={() => {
                  setShowDefaultFlow(true);
                }}
                style={{ height: 50, width: '100%' }}
              />
            </View>
          </ScrollView>
        </View>
      );
    }

    return (
      <>
        <View
          style={{
            flexDirection: 'row',
            paddingLeft: 16,
            paddingTop: 16,
          }}
        >
          <View>
            <Text type="title2">Create & Connect Phone Key</Text>
          </View>

          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={() => {
              setShowModal(false);
              onClose?.();
            }}
            style={{
              position: 'absolute',
              right: 16,
              top: 16,
            }}
          />
        </View>

        {currentStep > 1 ? (
          <ProgressIndicator currentStep={currentStep} numSteps={3} />
        ) : null}

        {renderStep()}

        <View
          style={{
            padding: 16,
            paddingTop: 0,
            gap: 24,
          }}
        >
          <View style={styles.bottomButtonsContainer}>
            <Button
              text={currentStep === 3 ? 'Finish' : 'Next'}
              type="primary"
              onPress={() => {
                if (currentStep === 3) {
                  setCurrentStep(1);
                  setShowModal(false);
                  onClose?.();
                } else {
                  setCurrentStep((prev) => prev + 1);
                }
              }}
              style={styles.button}
            />

            <Button
              text={currentStep === 1 ? 'Cancel' : 'Go Back'}
              type="ghost"
              onPress={() => {
                if (currentStep === 1) {
                  setShowModal(false);
                  onClose?.();
                } else {
                  setCurrentStep((prev) => prev - 1);
                }
              }}
              style={styles.button}
            />
          </View>
        </View>
      </>
    );
  };

  const renderStep = () => {
    let child = null;

    if (currentStep === 1) {
      child = (
        <View style={{ padding: 16, gap: 16 }}>
          <View style={{ gap: 8 }}>
            <Alert
              type={AlertType.WARNING}
              title="Important: You need to complete these steps for each car individually"
            />

            <Alert
              type={AlertType.WARNING}
              title="Important: You need to be the main owner for the car"
            />
          </View>

          <View>
            <Text type="bodyRegular">What is a Digital Key?</Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              Digital Keys are the way Tesla enables 3rd party systems (like
              Eon) to connect and communicate with your car. Data like mileage,
              charging, and vehicle control all pass through the Digital Key.
            </Text>
          </View>
        </View>
      );
    }

    if (currentStep === 2) {
      child = (
        <View style={{ padding: 16, gap: 16 }}>
          <Alert
            type={AlertType.WARNING}
            title="Important"
            body="You need to complete these steps for each car individually."
          />

          <Card style={{ padding: 16, gap: 16 }}>
            <View style={{ gap: 8 }}>
              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Step 1
              </Text>

              <Text type="bodyBold">
                Open the Tesla app and log in using the account of the primary
                owner*. Go to 'My product' and then open the car you want to
                connect (My products {'>'} Car's name).
              </Text>

              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                *Note: Only the main owner account (as opposed to a shared
                driver account) can create Phone Keys, so make sure you're
                signed into the primary account of the car you are connecting.
              </Text>
            </View>

            <View
              style={{
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'row',
                gap: 8,
              }}
            >
              <Image
                style={{
                  width: 120,
                  height: 260,
                }}
                source={tutorial1}
                contentFit="contain"
              />

              <Image
                style={{
                  width: 120,
                  height: 260,
                }}
                source={tutorial2}
                contentFit="contain"
              />
            </View>
          </Card>

          <Card style={{ padding: 16, gap: 16 }}>
            <View style={{ gap: 8 }}>
              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Step 2
              </Text>

              <Text type="bodyBold">
                While inside or near the car, open the Tesla app and tap Set Up
                Phone Key or navigate to: Security {'>'} Set Up Phone Key Follow
                the instructions in the app and on the car’s touchscreen.
              </Text>
            </View>

            <View style={{ gap: 8 }}>
              <Image
                style={{
                  width: 120,
                  height: 260,
                  alignSelf: 'center',
                }}
                source={tutorial3}
                contentFit="contain"
              />

              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                In-App Setup: Security {'>'} Set Up Phone Key
              </Text>
            </View>

            <View style={{ gap: 8 }}>
              <Image
                style={{
                  width: '100%',
                  height: 250,
                  alignSelf: 'center',
                  borderRadius: 8,
                }}
                source={tutorialVideo}
                contentFit="contain"
              />

              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Car setup. Note: You need to be physically near the car to
                enable this
              </Text>
            </View>
          </Card>

          <Card style={{ padding: 16, gap: 16 }}>
            <View style={{ gap: 8 }}>
              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Step 3
              </Text>

              <View style={{ gap: 4 }}>
                <Text type="bodyBold">
                  To confirm your phone key is working:
                </Text>

                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    marginBottom: 4,
                  }}
                >
                  <Text type="bodyBold" style={{ marginRight: 6 }}>
                    •
                  </Text>
                  <Text type="bodyBold">
                    Open the Tesla app and ensure you can lock and unlock the
                    car using your phone.
                  </Text>
                </View>

                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    marginBottom: 4,
                  }}
                >
                  <Text type="bodyBold" style={{ marginRight: 6 }}>
                    •
                  </Text>
                  <Text type="bodyBold">
                    You should see your phone key listed in the keys section on
                    your cars screen
                  </Text>
                </View>
              </View>
            </View>

            <View
              style={{
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Image
                style={{
                  width: '100%',
                  height: 260,
                  alignSelf: 'center',
                }}
                source={tutorial4}
                contentFit="contain"
              />
            </View>
          </Card>

          <View>
            <Pressable
              onPress={() => {
                const phoneNumber = '+18555987090';
                showActionSheetWithOptions(
                  {
                    options: ['Call', 'Text', 'Cancel'],
                    cancelButtonIndex: 2,
                  },
                  (buttonIndex) => {
                    if (buttonIndex === 0) {
                      Linking.openURL(`tel:${phoneNumber}`);
                    } else if (buttonIndex === 1) {
                      Linking.openURL(`sms:${phoneNumber}`);
                    }
                  }
                );
              }}
            >
              <Text
                type="caption1"
                style={{
                  color: theme.colors.text.secondary,
                  textDecorationLine: 'underline',
                }}
              >
                Additional questions? Text or call our support team at
                855-598-7090
              </Text>
            </Pressable>
          </View>
        </View>
      );
    }

    if (currentStep === 3) {
      child = (
        <View style={{ padding: 16, gap: 16 }}>
          <Alert
            type={AlertType.WARNING}
            title="Important"
            body="2020 and earlier Model S and X will not require a Digital Key to keep working at this time."
          />

          <Card style={{ padding: 16, gap: 16 }}>
            <View style={{ gap: 8 }}>
              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Step 1
              </Text>

              <Text type="bodyBold">
                From the same mobile device that you have created a phone key,
                navigate to the car you want to connect on the Tesla app.
              </Text>

              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Note: You can swipe across or navigate by going to My Products{' '}
                {'>'} Car's Name
              </Text>
            </View>

            <View
              style={{
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Image
                style={{
                  width: 120,
                  height: 260,
                }}
                source={tutorial5}
                contentFit="contain"
              />
            </View>
          </Card>

          <Card style={{ padding: 16, gap: 16 }}>
            <View style={{ gap: 8 }}>
              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Step 2
              </Text>

              <Text type="bodyBold">
                Tap on this button to create a Digital Key and follow the
                prompts.
              </Text>

              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Note: First make sure you have 'Phone as a key' set up on your
                car(s). If you don't have this, go back to 'Create a Phone Key'.
              </Text>

              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Make sure you've navigated to the correct car in your Tesla app
                before clicking on this link - this will link the car you
                currently have open in the Tesla app
              </Text>
            </View>

            <View>
              <Button
                text="Create Digital Key"
                type="primarySmall"
                onPress={handleCreateDigitalKey}
                style={{ width: 190 }}
              />
            </View>
          </Card>

          <Card style={{ padding: 16, gap: 16 }}>
            <View style={{ gap: 8 }}>
              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Step 3
              </Text>

              <Text type="bodyBold">
                Allow Eon to access to your vehicle and approve all permissions.
              </Text>
            </View>

            <View
              style={{
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Image
                style={{
                  width: 288,
                  height: 260,
                }}
                source={tutorial6}
                contentFit="contain"
              />
            </View>
          </Card>

          <Card style={{ padding: 16, gap: 16 }}>
            <View style={{ gap: 8 }}>
              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Step 4
              </Text>

              <Text type="bodyBold">
                Once you've successfully granted access to Eon, tap "Test
                Connection" below to ensure your Digital Key is working.
              </Text>
            </View>

            <View style={{ gap: 8 }}>
              <Button
                text="Test Connection"
                type="primarySmall"
                onPress={handleTestDigitalKey}
                style={{ width: 175 }}
                loading={isVerifyingVirtualKey}
              />

              {verifyVirtualKeyError ? (
                <Text type="caption1" style={{ color: theme.colors.error }}>
                  {verifyVirtualKeyError}
                </Text>
              ) : null}
            </View>
          </Card>

          <Alert
            type={AlertType.BRAND}
            title="Reminder"
            body="To connect another vehicle, navigate to that specific vehicle in the Tesla app, and restart this tutorial from step 1."
          />

          <Card style={{ padding: 16, gap: 16 }}>
            <View style={{ gap: 8 }}>
              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Step 5 (optional)
              </Text>

              <Text type="bodyBold">
                Allow Eon to access to your vehicle and approve all permissions.
              </Text>
            </View>

            <View
              style={{
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Image
                style={{
                  width: '100%',
                  height: 290,
                }}
                source={tutorial7}
                contentFit="contain"
              />
            </View>
          </Card>

          <View>
            <Pressable
              onPress={() => {
                const phoneNumber = '+18555987090';
                showActionSheetWithOptions(
                  {
                    options: ['Call', 'Text', 'Cancel'],
                    cancelButtonIndex: 2,
                  },
                  (buttonIndex) => {
                    if (buttonIndex === 0) {
                      Linking.openURL(`tel:${phoneNumber}`);
                    } else if (buttonIndex === 1) {
                      Linking.openURL(`sms:${phoneNumber}`);
                    }
                  }
                );
              }}
            >
              <Text
                type="caption1"
                style={{
                  color: theme.colors.text.secondary,
                  textDecorationLine: 'underline',
                }}
              >
                Additional questions? Text or call our support team at
                855-598-7090
              </Text>
            </Pressable>
          </View>
        </View>
      );
    }

    return (
      <ScrollView ref={scrollViewRef} showsVerticalScrollIndicator>
        {child}
      </ScrollView>
    );
  };

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={300}
      isVisible={showModal}
      hasBackdrop
      style={{
        ...styles.container,
        ...style,
      }}
      onBackdropPress={() => {
        setShowModal(false);
        onClose?.();
      }}
    >
      <View style={styles.containerView}>{renderBody()}</View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    margin: 0,
    justifyContent: {
      xs: 'flex-end',
      md: 'center',
    },
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 600,
    },
    width: {
      xs: '100%',
    },
  },
  containerView: {
    backgroundColor: theme.colors.background_2,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    gap: 24,
    borderRadius: 16,
    borderBottomLeftRadius: {
      xs: 0,
      md: 16,
    },
    borderBottomRightRadius: {
      xs: 0,
      md: 16,
    },
    maxHeight: '95%',
    paddingBottom: {
      xs: 16,
      md: 0,
    },
  },
  button: {
    width: {
      xs: '100%',
      md: 150,
    },
  },
  bottomButtonsContainer: {
    flexDirection: {
      xs: 'column',
      md: 'row-reverse',
    },
    gap: 4,
    alignItems: 'flex-end',
  },
  qrContainer: {
    display: {
      xs: 'none',
      md: 'flex',
    },
  },
  downloadApp: {
    display: {
      xs: 'flex',
      md: 'none',
    },
    flex: 1,
    height: 150,
  },
}));
